// Similar to conversations.slice.ts
// Manages:
// - Events collection state
// - Filtering state
// - Pagination state
// - Loading states
// - Selected event state
// - CRUD operations (fetchEvents, createEvent, updateEvent, deleteEvent)
// - Timeline-specific actions (fetchTimelineEvents, loadMoreEvents) 

import { createEntityAdapter, createSlice, createAsyncThunk, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { httpClient } from 'src/libs';
import { RootState } from 'src/store';

export interface EventAssociation {
  id: string;
  associable_type: string;
  associable_id: string;
}

export interface Event {
  id: string;
  account_id: string;
  event_type: string;
  source: string;
  message: string;
  description?: string;
  status: string;
  payload?: Record<string, any>;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  associations?: {
    contacts: any[];
    messages: any[];
    conversations: any[];
    notes: any[];
  };
  // Flag to indicate if this event has full data loaded
  _hasFullData?: boolean;
}

const eventsAdapter = createEntityAdapter<Event>({
  selectId: (event) => event.id,
  sortComparer: (a, b) => {
    // Sort by created_at descending (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  },
});

interface EventsState extends EntityState<Event> {
  loading: boolean;
  loadingFullData: boolean;
  error: any;
  totalEvents: number;
  hasMore: boolean;
  currentPage: number;
  perPage: number;
  selectedEventId: string | null;
  filters: {
    event_type?: string;
    source?: string;
    status?: string;
    associable_type?: string;
    associable_id?: string;
    created_after?: string;
    created_before?: string;
    search?: string;
    contact_id?: string;
    conversation_id?: string;
    message_id?: string;
    note_id?: string;
  };
}

const initialState: EventsState = eventsAdapter.getInitialState({
  loading: false,
  loadingFullData: false,
  error: null,
  totalEvents: 0,
  hasMore: false,
  currentPage: 1,
  perPage: 50,
  selectedEventId: null,
  filters: {},
});

interface FetchEventsParams {
  event_type?: string;
  source?: string;
  status?: string;
  associable_type?: string;
  associable_id?: string;
  created_after?: Date;
  created_before?: Date;
  page?: number;
  per_page?: number;
  limit?: number;
  order_by?: string;
  order_direction?: string;
  search?: string;
  contact_id?: string;
  conversation_id?: string;
  message_id?: string;
  note_id?: string;
  include_associations?: boolean;
  full_payload?: boolean;
  ids?: string[];
}

export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (params: FetchEventsParams = {}) => {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      per_page: (params.per_page || params.limit || 50).toString(),
      order_by: params.order_by || 'created_at',
      order_direction: params.order_direction || 'desc',
      full_payload: (params.full_payload || false).toString(),
      ...(params.event_type && { event_type: params.event_type }),
      ...(params.source && { source: params.source }),
      ...(params.status && { status: params.status }),
      ...(params.associable_type && { associable_type: params.associable_type }),
      ...(params.associable_id && { associable_id: params.associable_id }),
      ...(params.created_after && { created_after: params.created_after.toISOString() }),
      ...(params.created_before && { created_before: params.created_before.toISOString() }),
      ...(params.search && { search: params.search }),
      ...(params.contact_id && { contact_id: params.contact_id }),
      ...(params.conversation_id && { conversation_id: params.conversation_id }),
      ...(params.message_id && { message_id: params.message_id }),
      ...(params.note_id && { note_id: params.note_id }),
    });

    // Handle array parameters
    if (params.ids && params.ids.length > 0) {
      params.ids.forEach(id => queryParams.append('ids[]', id));
    }

    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events?${queryParams.toString()}`;
    const response = await httpClient.get(url);
    return response.data;
  }
);

export const fetchTimelineEvents = createAsyncThunk(
  'events/fetchTimelineEvents',
  async (params: { 
    before_timestamp?: string; 
    limit?: number; 
    event_types?: string[]; 
    sources?: string[] 
  } = {}) => {
    const queryParams = new URLSearchParams({
      limit: (params.limit || 50).toString(),
      include_associations: 'true',
      ...(params.before_timestamp && { before_timestamp: params.before_timestamp }),
    });

    // Handle arrays
    if (params.event_types) {
      params.event_types.forEach(type => queryParams.append('event_types[]', type));
    }
    if (params.sources) {
      params.sources.forEach(source => queryParams.append('sources[]', source));
    }

    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events/timeline?${queryParams}`;
    const response = await httpClient.get(url);
    return response.data;
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData: {
    event_type: string;
    source?: string;
    message?: string;
    description?: string;
    status?: string;
    payload?: Record<string, any>;
    metadata?: Record<string, any>;
    associate_with?: Array<{ type: string; id: string }>;
  }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events`;
    const response = await httpClient.post(url, eventData);
    return response.data;
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ eventId, updates }: { eventId: string; updates: Partial<Event> }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events/${eventId}`;
    const response = await httpClient.patch(url, updates);
    return response.data;
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (eventId: string) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events/${eventId}`;
    await httpClient.delete(url);
    return eventId;
  }
);

export const bulkDeleteEvents = createAsyncThunk(
  'events/bulkDeleteEvents',
  async (eventIds: string[]) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events/bulk_delete`;
    const response = await httpClient.post(url, { event_ids: eventIds });
    return response.data;
  }
);

// New action to fetch full data for specific events
export const fetchEventsFullData = createAsyncThunk(
  'events/fetchEventsFullData',
  async (eventIds: string[]) => {
    const queryParams = new URLSearchParams({
      full_payload: 'true'
    });
    
    eventIds.forEach(id => queryParams.append('ids[]', id));
    
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/events?${queryParams}`;
    const response = await httpClient.get(url);
    return response.data;
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    setSelectedEvent(state, action: PayloadAction<string | null>) {
      state.selectedEventId = action.payload;
    },
    setFilters(state, action: PayloadAction<EventsState['filters']>) {
      state.filters = action.payload;
    },
    clearFilters(state) {
      state.filters = {};
    },
    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setPerPage(state, action: PayloadAction<number>) {
      state.perPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        
        // If it's page 1, replace all events, otherwise append
        if (action.payload.meta.current_page === 1) {
          eventsAdapter.setAll(state, action.payload.events);
        } else {
          eventsAdapter.upsertMany(state, action.payload.events);
        }
        
        // Mark events based on whether they have full data
        const updatedEvents = action.payload.events.map((event: Event) => ({
          ...event,
          _hasFullData: action.payload.meta.full_payload
        }));
        eventsAdapter.upsertMany(state, updatedEvents);
        
        state.totalEvents = action.payload.meta.filtered_count;
        state.hasMore = action.payload.meta.has_more;
        state.currentPage = action.payload.meta.current_page;
        state.perPage = action.payload.meta.per_page;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch Timeline Events
      .addCase(fetchTimelineEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTimelineEvents.fulfilled, (state, action) => {
        state.loading = false;
        // Append to existing events for infinite scroll
        eventsAdapter.upsertMany(state, action.payload.events);
        state.hasMore = action.payload.has_more;
      })
      .addCase(fetchTimelineEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create Event
      .addCase(createEvent.fulfilled, (state, action) => {
        eventsAdapter.addOne(state, action.payload.event);
        state.totalEvents += 1;
      })
      // Update Event
      .addCase(updateEvent.fulfilled, (state, action) => {
        eventsAdapter.updateOne(state, {
          id: action.payload.event.id,
          changes: action.payload.event,
        });
      })
      // Delete Event
      .addCase(deleteEvent.fulfilled, (state, action) => {
        eventsAdapter.removeOne(state, action.payload);
        state.totalEvents -= 1;
      })
      // Bulk Delete Events
      .addCase(bulkDeleteEvents.fulfilled, (state, action) => {
        eventsAdapter.removeMany(state, action.payload.successful_deletes);
        state.totalEvents -= action.payload.successful_deletes.length;
      })
      // Fetch Full Data
      .addCase(fetchEventsFullData.pending, (state) => {
        state.loadingFullData = true;
      })
      .addCase(fetchEventsFullData.fulfilled, (state, action) => {
        state.loadingFullData = false;
        // Update events with full data
        const updatedEvents = action.payload.events.map((event: Event) => ({
          ...event,
          _hasFullData: true
        }));
        eventsAdapter.upsertMany(state, updatedEvents);
      })
      .addCase(fetchEventsFullData.rejected, (state) => {
        state.loadingFullData = false;
      });
  },
});

export const {
  setSelectedEvent,
  setFilters,
  clearFilters,
  setPage,
  setPerPage,
} = eventsSlice.actions;

export default eventsSlice.reducer;

// Selectors
export const {
  selectAll: selectAllEvents,
  selectById: selectEventById,
  selectIds: selectEventIds,
} = eventsAdapter.getSelectors((state: RootState) => state.events);

export const selectEventsLoading = (state: RootState) => state.events.loading;
export const selectEventsLoadingFullData = (state: RootState) => state.events.loadingFullData;
export const selectEventsError = (state: RootState) => state.events.error;
export const selectTotalEvents = (state: RootState) => state.events.totalEvents;
export const selectHasMoreEvents = (state: RootState) => state.events.hasMore;
export const selectCurrentPage = (state: RootState) => state.events.currentPage;
export const selectPerPage = (state: RootState) => state.events.perPage;
export const selectSelectedEventId = (state: RootState) => state.events.selectedEventId;
export const selectEventFilters = (state: RootState) => state.events.filters;
export const selectSelectedEvent = (state: RootState) => {
  const selectedId = state.events.selectedEventId;
  return selectedId ? selectEventById(state, selectedId) : null;
}; 
